import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BatteryCharging, Camera, Sparkles } from "lucide-react";
import { useLang } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

// Per-service stage overlay animations (transform/opacity only).
const StageOverlay = ({ mode }) => {
  switch (mode) {
    case "screen":
    case "diagnostics":
      return (
        <motion.div
          className="absolute inset-x-[12%] h-[3px] rounded-full bg-volt"
          style={{ boxShadow: "0 0 18px rgba(233,180,76,0.9)" }}
          animate={{ top: ["14%", "84%", "14%"], opacity: [0, 1, 0] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        />
      );
    case "battery":
      return (
        <motion.div
          className="absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2 text-volt"
          animate={{ scale: [1, 1.18, 1], opacity: [0.65, 1, 0.65] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <BatteryCharging className="h-12 w-12 drop-shadow-[0_0_16px_rgba(233,180,76,0.9)]" />
        </motion.div>
      );
    case "charging":
      return (
        <>
          {[0, 1].map((i) => (
            <motion.span
              key={i}
              className="absolute left-1/2 bottom-[6%] h-10 w-16 -translate-x-1/2 rounded-[50%] border-2 border-cyan"
              animate={{ scale: [0.5, 2.1], opacity: [0.85, 0] }}
              transition={{ duration: 1.6, delay: i * 0.8, repeat: Infinity, ease: "easeOut" }}
            />
          ))}
        </>
      );
    case "backglass":
      return (
        <motion.div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute inset-y-[-25%] w-20"
            animate={{ x: ["-140%", "560%"] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.6 }}
          >
            <div
              className="h-full w-full"
              style={{
                background: "linear-gradient(100deg, transparent, rgba(255,255,255,0.35) 50%, transparent)",
                transform: "skewX(-16deg)",
              }}
            />
          </motion.div>
        </motion.div>
      );
    case "camera":
      return (
        <motion.div
          className="absolute left-[24%] top-[10%] text-cyan"
          animate={{ opacity: [0.4, 1, 0.4], scale: [0.94, 1.05, 0.94] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <Camera className="h-10 w-10 drop-shadow-[0_0_14px_rgba(79,179,191,0.9)]" />
          <span className="absolute -inset-3 rounded-lg border border-cyan/70" />
        </motion.div>
      );
    case "water":
      return (
        <>
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="absolute left-1/2 top-1/2 h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-cyan"
              animate={{ scale: [0.3, 4.4], opacity: [0.75, 0] }}
              transition={{ duration: 2.1, delay: i * 0.7, repeat: Infinity, ease: "easeOut" }}
            />
          ))}
        </>
      );
    case "software":
      return (
        <div className="absolute inset-x-[30%] bottom-[10%] flex h-10 items-end justify-center gap-1.5">
          {[0.9, 0.5, 1.1, 0.7, 1.3, 0.6].map((d, i) => (
            <motion.span
              key={i}
              className="w-1.5 rounded-full bg-magenta"
              style={{ boxShadow: "0 0 10px rgba(124,131,219,0.8)" }}
              animate={{ height: ["26%", "100%", "38%"] }}
              transition={{ duration: d, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }}
            />
          ))}
        </div>
      );
    case "data":
      return (
        <>
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.span
              key={i}
              className="absolute bottom-[12%] h-1.5 w-1.5 rounded-full bg-volt"
              style={{ left: `${32 + i * 9}%`, boxShadow: "0 0 8px rgba(233,180,76,0.9)" }}
              animate={{ y: [0, -150], opacity: [0, 1, 0] }}
              transition={{ duration: 1.8, delay: i * 0.28, repeat: Infinity, ease: "easeOut" }}
            />
          ))}
        </>
      );
    case "cleaning":
      return (
        <motion.div
          className="absolute left-1/2 top-[30%] -translate-x-1/2 text-gold-soft"
          animate={{ rotate: [0, 25, -15, 0], scale: [1, 1.25, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkles className="h-11 w-11 drop-shadow-[0_0_16px_rgba(201,155,63,0.95)]" />
        </motion.div>
      );
    default:
      return null;
  }
};

// Node positions (percent of stage) for the 11 services.
const NODES = {
  screen: [50, 30],
  battery: [50, 56],
  charging: [50, 84],
  backglass: [34, 48],
  camera: [30, 14],
  water: [66, 46],
  software: [50, 68],
  data: [68, 64],
  laptop: [82, 26],
  diagnostics: [18, 34],
  cleaning: [80, 70],
};

// Interactive diagnostic stage: the cracked iPhone centerpiece reacts to the
// service list; pulse nodes are two-way synced hot spots.
export const RepairStage = ({ activeId, services, onPick }) => {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const id = activeId || "screen";
  // Owner-picked stage visuals: 4K MacBook for laptop repair, the cracked
  // modern iPhone for diagnostics, the classic cracked iPhone otherwise.
  const visual =
    id === "laptop"
      ? { key: "laptop", src: "/media/macbook-desk.jpg", photo: true, alt: "MacBook laptop on a work desk" }
      : id === "diagnostics"
        ? { key: "diag", src: "/phones/ip17-cracked-screen.png", photo: false, alt: "iPhone Pro Max with a shattered screen" }
        : { key: "cracked", src: "/devices/cracked-iphone.png", photo: false, alt: "iPhone with cracked back glass" };

  return (
    <div
      data-testid="repair-stage"
      className="glass-liquid relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-[20px]"
    >
      <div className="pointer-events-none absolute -top-20 right-0 h-56 w-56 rounded-full bg-accent/20 blur-3xl" aria-hidden="true" />

      <AnimatePresence mode="wait">
        <motion.div
          key={visual.key}
          className="relative z-10 flex items-center justify-center"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9, rotate: -4 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, rotate: 3 }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          {visual.photo ? (
            <img
              src={visual.src}
              alt={visual.alt}
              data-testid="repair-lab-cracked-centerpiece"
              draggable="false"
              className="h-[52%] w-[86%] rounded-[18px] border border-ink/15 object-cover shadow-[0_24px_60px_-18px_rgba(0,0,0,0.6)]"
              style={{ filter: "drop-shadow(0 0 44px rgba(61,111,224,0.35))" }}
            />
          ) : (
            <img
              src={visual.src}
              alt={visual.alt}
              data-testid="repair-lab-cracked-centerpiece"
              draggable="false"
              className={visual.key === "diag" ? "h-[70%] w-auto" : "h-[74%] w-auto"}
              style={{ filter: `drop-shadow(0 0 44px ${visual.key === "diag" ? "rgba(124,131,219,0.4)" : "rgba(46,107,230,0.42)"})` }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {!reduce && (
        <div className="absolute inset-0 z-20" aria-hidden="true">
          <AnimatePresence mode="wait">
            <motion.div
              key={id}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StageOverlay mode={id} />
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* diagnostic pulse nodes */}
      {services.map((s) => {
        const pos = NODES[s.id] || [50, 50];
        const active = s.id === id;
        return (
          <button
            key={s.id}
            type="button"
            data-testid={`service-node-${s.id}`}
            aria-label={t(`lab.services.${s.id}.title`)}
            aria-pressed={active}
            onClick={() => onPick(s.id)}
            className="group absolute z-30 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${pos[0]}%`, top: `${pos[1]}%` }}
          >
            <span
              className={`block h-3 w-3 rounded-full transition-all duration-300 ${
                active ? "scale-125 bg-volt shadow-[0_0_14px_rgba(233,180,76,0.95)]" : "bg-cyan/80 group-hover:bg-volt"
              }`}
            />
            {!reduce && (
              <span
                className={`absolute inset-0 rounded-full border ${
                  active ? "border-volt" : "border-cyan/60"
                } animate-ping [animation-duration:2.2s]`}
              />
            )}
          </button>
        );
      })}

      <div className="absolute bottom-4 left-0 right-0 z-30 text-center">
        <AnimatePresence mode="wait">
          <motion.span
            key={id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="font-mono text-[10px] uppercase tracking-[0.26em] text-volt/90"
          >
            {t(`lab.services.${id}.title`)}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
};
